import * as React from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { TableHead } from "./table";

export type SortDirection = 'asc' | 'desc' | null;

interface SortableTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortKey?: string;
  currentSortKey?: string | null;
  currentSortDirection?: SortDirection;
  onSort?: (key: string) => void;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}

export function SortableTableHead({
  sortKey,
  currentSortKey,
  currentSortDirection,
  onSort,
  children,
  className,
  align = 'left',
  ...props
}: SortableTableHeadProps) {
  const isSortable = sortKey && onSort;
  const isActive = sortKey === currentSortKey;

  const handleClick = () => {
    if (isSortable) {
      onSort(sortKey);
    }
  };

  const alignmentClasses = {
    left: 'justify-start text-left',
    center: 'justify-center text-center',
    right: 'justify-end text-right',
  };

  return (
    <TableHead
      className={cn(
        "font-bold text-foreground",
        isSortable && "cursor-pointer select-none group",
        align === 'right' && "text-right",
        align === 'center' && "text-center",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      <span className={cn("inline-flex items-center gap-1", alignmentClasses[align])}>
        <span>{children}</span>
        {isSortable && (
          <span className={cn(
            "transition-opacity text-foreground font-bold inline-flex",
            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-50"
          )}>
            {isActive && currentSortDirection === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5 stroke-[2.5]" />
            ) : isActive && currentSortDirection === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5 stroke-[2.5]" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 stroke-[2.5]" />
            )}
          </span>
        )}
      </span>
    </TableHead>
  );
}
